import { useMutation } from 'react-query';
import { v4 as uuidv4 } from 'uuid';

// our axios
import adminAxiosInstance from 'services/API/ADMINAPI';

// our api urls
import { getPylonPurchaseRateUrl } from 'shared/api-urls';

// our utils
import countyToFipCode from 'shared/constants/mortgage/countyToFipCode';

// local consants
const PYLON_PROPERTY_TYPE_TO_VALUE = {
	'Single Family': 'SINGLE_FAMILY',
	'Multi-unit': 'TWO_TO_FOUR_FAMILY',
	'Manufactured home': 'MANUFACTURED_HOME',
	Condo: 'CONDO'
};

const PYLON_OCCUPANCY_TYPE_TO_VALUE = {
	'Primary Residence': 'PRIMARY_RESIDENCE',
	'Second Home': 'SECOND_HOME',
	'Investment Property': 'INVESTMENT'
};

// local helper functions
const getTermFromDropDown = (dropDownValue) => {
	const splitValue = dropDownValue.split(' ');
	const termLength = +splitValue[0];
	return termLength;
};

// Finds the best product that is eligible from pylon
const bestProduct = (rates) => {
	const bestRate = rates.reduce((best, current) => {
		return Math.abs(best.totalPoints) < Math.abs(current.totalPoints)
			? best
			: current;
	}, rates[0]);

	const {
		rateId,
		apr,
		closingCosts,
		monthlyPayment,
		principal,
		rate,
		totalPoints,
		cashFromToBorrower,
		term
	} = bestRate;

	return {
		rateId,
		apr,
		pointsPaid: totalPoints,
		fees: closingCosts,
		monthlyPayment,
		noteRate: rate,
		totalLoanAmount: principal,
		cashToClose: cashFromToBorrower,
		term
	};
};

const formatPylonPurchaseRateDetails = ({
	county,
	creditScore,
	firstTimeHomeBuyer,
	loanProduct,
	monthlyDebt,
	monthlyIncome,
	occupancyType,
	outOfPocketMax,
	propertyType,
	propertyValue,
	selfEmployed,
	state
}) => {
	const isFirstTimeHomeBuyer = firstTimeHomeBuyer === 'Yes';
	const isBorrowerSelfEmployed = selfEmployed === 'Yes';
	const fipsCountyCode = countyToFipCode(county, state);

	const neighborhoodHousingType = PYLON_PROPERTY_TYPE_TO_VALUE[propertyType];

	const propertyUsageType = PYLON_OCCUPANCY_TYPE_TO_VALUE[occupancyType];

	const pylonRateDetails = {
		fipsCountyCode,
		isFirstTimeHomeBuyer,
		isBorrowerSelfEmployed,
		monthlyDebt: +monthlyDebt,
		monthlyIncome: +monthlyIncome,
		neighborhoodHousingType,
		outOfPocketMax: +outOfPocketMax,
		propertyUsageType,
		qualifyingFicoScore: +creditScore,
		rateLockDays: 30,
		salesContractAmount: +propertyValue,
		loanTermYears: getTermFromDropDown(loanProduct)
	};

	return pylonRateDetails;
};

function useMutateGetPylonPurchaseRate() {
	return useMutation(async (rateDetails) => {
		const { loanProduct } = rateDetails;
		const isARMProduct = loanProduct && loanProduct.includes('ARM');

		const formattedPylonRateDetails =
			formatPylonPurchaseRateDetails(rateDetails);

		const propertyValueUsed = rateDetails?.propertyValue ?? 0;

		const response = await adminAxiosInstance.post(
			getPylonPurchaseRateUrl,
			formattedPylonRateDetails
		);

		const pylonOffers = response.data.products;

		const eligibleOffers = pylonOffers.filter(
			(offer) => offer?.eligibleProductPricingFields !== null
		);

		const flatOffers = eligibleOffers.flatMap(
			(offer) => offer.eligibleProductPricingFields
		);

		const productSpecificOffers = flatOffers.filter((offer) => {
			const isArmOffer = offer.adjustableRateDetail !== null;

			if (isARMProduct) {
				return isArmOffer;
			}

			return !isArmOffer;
		});

		const flatOffersWithId = productSpecificOffers
			.map((offer) => ({
				...offer,
				id: uuidv4(),
				type: 'Pylon',
				lenderName: offer.product.description,
				propertyValue: propertyValueUsed
			}))
			.map((offer) => ({
				...offer,
				...bestProduct(offer.rates)
			}));

		return flatOffersWithId;
	});
}

export default useMutateGetPylonPurchaseRate;
